import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { CLOSED_TO_VISITORS_MESSAGE, type InteriorBoard, type PropertyPublic } from "@townsy/contracts";
import { errorMessage } from "../api/client";
import { cardPhrase } from "../commerce/gates";
import { useMyHomeQuery, useMyPermissionsQuery, usePropertyQuery } from "../api/queries";
import { useTownSession } from "../app/TownSessionContext";
import { districtName } from "../lib/directoryFilters";
import { HOME_STAGE_LABELS, TEMPLATE_LABELS, formatCents, formatRelative, formatUtcDateTime } from "../lib/format";
import { PROPERTY_TYPE_LABEL, homeOf, homeTeamOf, isPlotForSale, logoFallback, propertyDisplayName, propertyFullName } from "../lib/propertyLabels";
import { formatCoins } from "../lib/wallet";
import { selectIsGuest, useTownStore } from "../state/store";
import { BoardEditorDialog } from "./BoardEditorDialog";
import { StoreThemeThumbnail } from "./StoreThemeDialog";
import { STORE_THEME_OPTIONS, canPickStoreTheme, storeThemeLabel } from "./storeThemeLogic";
import { OUT_OF_CAR_FIRST } from "../net/vehicles";
import { LogoTile } from "./LogoTile";
import { PropertyHistory } from "./PropertyHistory";
import { PropertyStats } from "./PropertyStats";
import { CommercialOfferSection } from "./checkout/CommercialOffer";
import { useTestPrices } from "./testPrices";
import { useDollarGate } from "./checkout/useDollarGate";
import { exteriorLookSummary, interiorLookSummary } from "../homes/lookPalette";
import { VisitWebsiteLink } from "./VisitWebsiteLink";
import { currentSignLabel, isPlaceholderSign } from "./advertising/PlacementDialog";
import { PropertyTasksSection } from "./rewards/StoreTasks";
import { StoreSetupPanelSection } from "./storeSetup/StoreSetupEntry";
import { ClosedNotice, StoreVisitorsControl, closedToViewer, isPropertyOwner, useViewerActorId } from "./VisitorAccess";

/**
 * Selected property details (docs/01 section 6, docs/00 R03). Selection never sends traffic
 * anywhere: only the intentional "Visit website" anchor opens the destination. Three kinds:
 * commercial (business, prices, store board), civic (Townsy Real Estate: name, description,
 * Enter — no prices, website or Buy) and residential (for sale at the office, or an owned home).
 */
export function PropertyPanel() {
  const navigate = useNavigate();
  const session = useTownSession();
  const id = useTownStore((s) => s.ui.selectedPropertyId);
  const stored = useTownStore((s) => (s.ui.selectedPropertyId ? (s.properties[s.ui.selectedPropertyId] ?? null) : null));
  const fetched = usePropertyQuery(id && !stored ? id : null);
  const item: PropertyPublic | null = stored ?? fetched.data ?? null;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const trackedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!id) {
      trackedFor.current = null;
      return;
    }
    const t = requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
    // One panel_view candidate per opening; wait for the session when the deep link beat bootstrap.
    // (Civic buildings are filtered inside analytics: they are not advertising surfaces.)
    if (session && trackedFor.current !== id) {
      trackedFor.current = id;
      session.analytics.trackPanelView(id);
    }
    return () => cancelAnimationFrame(t);
  }, [id, session]);

  if (!id) return null;
  const close = () => navigate("/");
  const name = propertyDisplayName(item);
  // ADR-0044: a stadium's home team ("Titans Field — home of the Townsy Titans").
  const fullName = propertyFullName(item);
  const homeTeam = item?.type === "CIVIC" ? homeTeamOf(item.civic) : null;

  return (
    <section className="panel panel-right" role="region" aria-labelledby="property-panel-title" data-testid="property-panel">
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-header">
        <LogoTile placement={item?.placement} fallback={logoFallback(item)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="property-panel-title" tabIndex={-1} ref={headingRef} style={{ outline: "none" }} title={fullName !== name ? fullName : undefined}>
            {name}
          </h2>
          {homeTeam ? (
            <div className="small" data-testid="panel-home-team">
              {homeOf(homeTeam).replace(/^h/, "H")}
            </div>
          ) : null}
          <div className="small muted">
            {item ? `${item.address} · ${PROPERTY_TYPE_LABEL[item.type]}` : id}
            {item?.districtId ? (
              <>
                {" · "}
                <span data-testid="panel-district">{districtName(item.districtId)}</span>
              </>
            ) : null}
            {item?.placement?.isDemo ? (
              <>
                {" "}
                · <span className="badge">Demo</span>
              </>
            ) : null}
          </div>
        </div>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close property panel" onClick={close}>
          ×
        </button>
      </div>
      <div className="panel-body">
        {!item ? (
          fetched.error ? (
            <p>
              <span className="error-text">Couldn't load this property.</span> {errorMessage(fetched.error)}
            </p>
          ) : (
            <p className="muted">Loading…</p>
          )
        ) : item.type === "COMMERCIAL" ? (
          <CommercialDetails item={item} />
        ) : item.type === "CIVIC" ? (
          <CivicDetails item={item} />
        ) : (
          <ResidentialDetails item={item} />
        )}
      </div>
    </section>
  );
}

/**
 * Enter / "Take me there". At the door in Walk mode, Enter asks the server (the same as walking through it). Anywhere
 * else (Walk mode away from the door, or inside another building) "Take me there" is the explicit fast travel that
 * Overview's Enter is (docs/03 section 4): the server authorizes the destination and picks a safe spot inside. A place
 * in another district than the one the visitor is standing in (world v5) is always "Take me there", and the hint names
 * its district. ADR-0042: every district is on the one town street, so there is no district notice or travel step; the
 * header and the District row name the place's district.
 */
function EnterButton({ item }: { item: PropertyPublic }) {
  const session = useTownSession();
  const { pending, mode, nearby, roomKind, roomPropertyId, driving, currentDistrict } = useTownStore(
    useShallow((s) => ({
      pending: s.ui.transitionPending,
      mode: s.ui.mode,
      nearby: s.ui.nearbyEntranceId,
      roomKind: s.room.kind,
      roomPropertyId: s.room.propertyId,
      driving: s.ui.drivingVehicleId !== null,
      currentDistrict: s.districtId,
    })),
  );
  const inside = roomKind === "interior";
  const here = inside && roomPropertyId === item.id;
  const otherDistrict = Boolean(item.districtId && item.districtId !== currentDistrict) && !here;
  const there = districtName(item.districtId);
  // ADR-0019: the owner of a closed home or store may still go in (the public DTO marks it not enterable for visitors;
  // the room-ticket API and the room admit the owner). Everyone else sees why Enter is disabled.
  const myHomeId = useMyHomeId();
  const viewer = useViewerActorId();
  const owner = myHomeId === item.id || isPropertyOwner(item, viewer);
  const closedForMe = closedToViewer(item, viewer, owner);
  const ownerOfClosed = item.access === "closed" && owner && item.status === "OWNED";
  const open = (item.enterable && item.permittedActions.includes("enter")) || ownerOfClosed;
  const takeMeThere = !here && (otherDistrict || inside || (mode === "walk" && nearby !== item.id));
  const canEnter = open && Boolean(session) && !here && !driving;
  const hint = closedForMe
    ? CLOSED_TO_VISITORS_MESSAGE
    : !open
      ? "Not open to visitors right now"
      : here
        ? "You're inside"
        : driving
          ? OUT_OF_CAR_FIRST
          : otherDistrict
            ? `In ${there}. Fast travel: the server picks a safe spot inside.`
            : takeMeThere
              ? "Fast travel: the server picks a safe spot inside. In Walk mode you can also walk through the door."
              : mode === "overview"
                ? "Explicit fast travel: the server picks a safe spot inside"
                : undefined;
  const label = pending ? "Entering…" : takeMeThere ? "Take me there" : mode === "overview" ? "Enter (fast travel)" : "Enter";
  return (
    <div className="stack" style={{ gap: 4 }}>
      <button
        type="button"
        className={takeMeThere ? "btn btn-primary" : "btn"}
        disabled={!canEnter || pending}
        onClick={() => void (takeMeThere ? session?.fastTravel(item.id) : session?.enterProperty(item.id))}
        title={hint}
        data-testid="panel-enter"
      >
        {label}
      </button>
      {hint ? (
        <span className="hint" data-testid={closedForMe ? "enter-closed-reason" : undefined}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** Owner (or staff on Townsy demo stores) can edit the TV board inside the store. */
function StoreBoardSection({ item }: { item: PropertyPublic }) {
  const session = useTownSession();
  const isGuest = useTownStore(selectIsGuest);
  const perms = useMyPermissionsQuery(item.id, !isGuest);
  const [editing, setEditing] = useState(false);
  const pushToast = useTownStore((s) => s.pushToast);
  if (isGuest || !perms.data?.canEditBoard) return null;
  const board = item.interiorBoard;
  return (
    <div className="section" data-testid="store-board-section">
      <div className="section-title">Store board</div>
      <p className="small" style={{ margin: 0 }}>
        {board ? (
          <>
            <strong>{board.title}</strong>
            {board.body ? ` — ${board.body}` : ""}
          </>
        ) : (
          <span className="muted">No message yet. Visitors see a neutral screen.</span>
        )}
      </p>
      <button type="button" className="btn btn-sm" style={{ marginTop: 6 }} onClick={() => setEditing(true)} data-testid="edit-store-board">
        Edit store board
      </button>
      <span className="hint"> Shown on the TV inside your store.</span>
      {editing ? (
        <BoardEditorDialog
          propertyId={item.id}
          businessName={item.placement?.businessName ?? item.address}
          board={board}
          onClose={() => setEditing(false)}
          onSaved={(saved: InteriorBoard) => {
            setEditing(false);
            pushToast("Store board saved. Visitors inside see it now.", "success");
            session?.applySavedBoard(item.id, saved);
          }}
          onReloadLatest={async () => (await session?.refreshProperty(item.id))?.interiorBoard ?? null}
        />
      ) : null}
    </div>
  );
}

/**
 * The owner's store interior (ADR-0006 #5, ADR-0008 #4), near the top of the store panel. Offered
 * only when the property read made with the visitor's own session lists `edit_interior` (the
 * current commercial owner); never from room data. "Change interior" opens the page's one picker.
 */
function StoreInteriorSection({ item }: { item: PropertyPublic }) {
  const signedIn = useTownStore((s) => Boolean(s.bootstrap?.session.userId));
  const own = usePropertyQuery(signedIn ? item.id : null);
  const openThemePicker = useTownStore((s) => s.openThemePicker);
  if (!canPickStoreTheme(own.data, signedIn)) return null;
  const current = own.data?.interiorTheme ?? item.interiorTheme ?? "general";
  const description = STORE_THEME_OPTIONS.find((o) => o.id === current)?.description ?? "";
  return (
    <div className="section owner-interior" data-testid="store-theme-section">
      <div className="section-title">Your store's interior</div>
      <div className="owner-interior-row">
        <span className="owner-interior-thumb">
          <StoreThemeThumbnail themeId={current} />
        </span>
        <div style={{ minWidth: 0 }}>
          <p className="small" style={{ margin: 0 }} data-testid="store-theme-current">
            <strong>{storeThemeLabel(current) ?? current}</strong>
            {description ? ` — ${description}` : ""}
          </p>
          <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 6 }} onClick={() => openThemePicker(item.id, "panel")} data-testid="change-store-theme">
            Change interior
          </button>
        </div>
      </div>
      <span className="hint">Any of the {STORE_THEME_OPTIONS.length} presets fits any business. Free; visitors inside see the change right away.</span>
    </div>
  );
}

/**
 * The owner's Open / Closed switch and "Send visitors out" (ADR-0019), for the current owner only (their own property
 * read lists `edit_interior`, the same current-owner rule the server applies to these commands).
 */
function StoreVisitorsSection({ item }: { item: PropertyPublic }) {
  const signedIn = useTownStore((s) => Boolean(s.bootstrap?.session.userId));
  const own = usePropertyQuery(signedIn ? item.id : null);
  if (!canPickStoreTheme(own.data, signedIn)) return null;
  return (
    <div className="section" data-testid="store-visitors-section">
      <div className="section-title">Your store's visitors</div>
      <StoreVisitorsControl item={item} />
    </div>
  );
}

/**
 * The owner's store sign (docs/01 "Manage placement", ADR-0009 #8): offered only to the current owner (their own
 * property read lists `edit_interior`, a current-owner-only action). Applying a design goes through the page's one
 * placement picker; the server re-checks ownership and approval, so a former owner is refused (A25).
 */
function StoreSignSection({ item }: { item: PropertyPublic }) {
  const signedIn = useTownStore((s) => Boolean(s.bootstrap?.session.userId));
  const own = usePropertyQuery(signedIn ? item.id : null);
  const openPlacementPicker = useTownStore((s) => s.openPlacementPicker);
  if (!canPickStoreTheme(own.data, signedIn)) return null;
  return <StoreSignView current={own.data ?? item} onShow={() => openPlacementPicker({ propertyId: item.id })} />;
}

/**
 * The section itself (exported for server-rendered tests). Since ADR-0020 the store screen ("Edit your store", first in
 * the panel) is the way to put a name and logo on the store; this section remains for designs made in the advertiser
 * tools, so it sits lower and plainer.
 */
export function StoreSignView({ current, onShow }: { current: Pick<PropertyPublic, "placement">; onShow(): void }) {
  return (
    <div className="section" data-testid="store-sign-section">
      <div className="section-title">Sign designs from the advertiser tools</div>
      <p className="small" style={{ margin: 0 }}>
        Now showing {currentSignLabel(current)}.
      </p>
      <div className="row" style={{ marginTop: 6 }}>
        <button type="button" className="btn btn-sm" onClick={onShow} data-testid="manage-placement">
          Show a design from my library
        </button>
        <Link className="btn btn-sm btn-ghost" to="/advertise?tab=designs" data-testid="create-design">
          Advertiser tools
        </Link>
        <Link className="btn btn-sm btn-ghost" to="/advertise?tab=stats">
          My performance
        </Link>
      </div>
      <span className="hint">
        {isPlaceholderSign(current) ? "Use Set up your store above to put your name and logo on the store. " : ""}A design from your library changes the sign for everyone right away.
      </span>
    </div>
  );
}

function CommercialDetails({ item }: { item: PropertyPublic }) {
  const setUi = useTownStore((s) => s.setUi);
  const testPrices = useTestPrices();
  const [statsOpen, setStatsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const viewer = useViewerActorId();
  const p = item.placement;
  return (
    <div className="stack">
      {closedToViewer(item, viewer) ? <ClosedNotice /> : null}
      {/* ADR-0020: the owner's one screen for name, logo, website, description and the sign inside comes first. */}
      <StoreSetupPanelSection item={item} />
      <StoreInteriorSection item={item} />
      <StoreVisitorsSection item={item} />

      {p ? (
        <>
          {p.shortDescription ? <p style={{ margin: 0 }}>{p.shortDescription}</p> : null}
          {p.hasWebsite && item.permittedActions.includes("visit_website") ? (
            <div className="row">
              <VisitWebsiteLink propertyId={item.id} host={p.destinationHost} eager />
              <span className="small muted">Opens {p.destinationHost || "the business website"} in a new tab</span>
            </div>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>
              No approved website link for this listing.
            </p>
          )}
          {p.isDemo ? (
            <p className="hint" style={{ margin: 0 }}>
              Demo business: a placeholder listing used while Townsy is in development. Its website is a Townsy page.
            </p>
          ) : null}
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          This building has no business placement yet. It can be bought below.
        </p>
      )}

      <EnterButton item={item} />

      {/* Founder 2026-10-27: the Buy (or takeover) button sits right under Enter, in view without scrolling, not below the
          sponsored tasks and the ownership facts. */}
      <div className="section">
        <CommercialOfferSection item={item} />
      </div>

      <PropertyTasksSection item={item} />

      <StoreBoardSection item={item} />

      <StoreSignSection item={item} />

      <div className="section">
        <div className="section-title">Ownership</div>
        <dl className="kv">
          <dt>Owner</dt>
          <dd>{item.ownerLabel ?? "Unowned"}</dd>
          <dt>Current value</dt>
          <dd className="price" title="What the current owner paid (eligible subtotal, including reused credit)">
            {formatCents(item.currentBasisCents)}
          </dd>
          <dt>Next price</dt>
          <dd className="price" title="What a new buyer pays to take over this building">
            {formatCents(item.nextPriceCents)}
          </dd>
          <dt>Takeovers</dt>
          <dd>{item.takeoverCount}</dd>
          <dt>Last change</dt>
          <dd title={formatUtcDateTime(item.lastChangeAt)}>{formatRelative(item.lastChangeAt)}</dd>
          <dt>Building</dt>
          <dd>
            {p ? (TEMPLATE_LABELS[p.template] ?? p.template) : "—"}
            {` · height stage ${item.heightStage}`}
          </dd>
          <dt>Interior</dt>
          <dd data-testid="store-theme-label">{storeThemeLabel(item.interiorTheme) ?? "—"}</dd>
          <dt>Status</dt>
          <dd>{item.status.toLowerCase()}</dd>
        </dl>
        <p className="hint" style={{ marginTop: 6 }}>
          {testPrices ? "Development test prices — not approved live prices. " : null}The current value is the owner's basis; the next price is what a new
          buyer must pay. A displaced owner gets their eligible amount back as Townsy Credit (not cash). The building grows one stage
          with each completed purchase; height never implies traffic.
        </p>
      </div>

      <div className="section">
        <button type="button" className="btn btn-sm" aria-expanded={statsOpen} onClick={() => setStatsOpen((v) => !v)}>
          {statsOpen ? "Hide statistics" : "Statistics"}
        </button>
        {statsOpen ? (
          <div style={{ marginTop: 8 }}>
            <PropertyStats propertyId={item.id} active={statsOpen} />
          </div>
        ) : null}
      </div>

      <div className="section">
        <button type="button" className="btn btn-sm" aria-expanded={historyOpen} onClick={() => setHistoryOpen((v) => !v)}>
          {historyOpen ? "Hide history" : "History"}
        </button>
        {historyOpen ? (
          <div style={{ marginTop: 8 }}>
            <PropertyHistory propertyId={item.id} active={historyOpen} />
          </div>
        ) : null}
      </div>

      <div className="section">
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setUi({ reportOpen: true })}>
          Report this listing
        </button>
      </div>
    </div>
  );
}

const CIVIC_COPY: Record<string, { show: string; hint: string }> = {
  real_estate_office: { show: "Show plot listings", hint: "Run by Townsy. Residential plots are listed and sold inside." },
  home_builders: { show: "Show Home Builders", hint: "Run by Townsy. Home upgrades (tent to castle), yard features and paint are sold inside." },
  furnishings: { show: "Show Furnishings", hint: "Run by Townsy. Furniture and inside styles for your home are sold inside." },
};

/**
 * Sports Park stadiums (ADR-0044): free to watch; predictions use free play chips only and need an account (ADR-0048).
 */
const STADIUM_HINT =
  "Run by Townsy. Free to watch: sit in the stands and cheer. Predictions use free play chips only: nothing is bought or won. Sign up (18+) to predict. The controls are at the bottom of the screen inside.";

/**
 * Attractions (ADR-0016): run by Townsy, free to play; the controls are in the room itself (VenueHudHost). Playing with
 * play chips needs an account (ADR-0048); anyone can walk in and watch.
 */
const VENUE_HINT: Record<string, string> = {
  casino: "Run by Townsy. Free play chips only: nothing is bought or won. Anyone can watch; sign up (18+) to play. The game controls are at the bottom of the screen inside.",
  race_track: "Run by Townsy. Free play chips only: nothing is bought or won. Anyone can watch; sign up (18+) to bet. The betting window is at the bottom of the screen inside.",
  sports_center: "Run by Townsy. Free to play. The game controls are at the bottom of the screen inside.",
  truck_arena: "Run by Townsy. Free to drive. The controls are at the bottom of the screen inside.",
  // Sports Park stadiums (ADR-0044).
  football_stadium: STADIUM_HINT,
  soccer_stadium: STADIUM_HINT,
  baseball_stadium: STADIUM_HINT,
  basketball_arena: STADIUM_HINT,
  hockey_arena: STADIUM_HINT,
};

/** Townsy-run building: name, description, Enter. Never priced, never purchasable, no website. */
function CivicDetails({ item }: { item: PropertyPublic }) {
  const inside = useTownStore((s) => s.room.kind === "interior" && s.room.propertyId === item.id);
  const setUi = useTownStore((s) => s.setUi);
  const kind = item.civic?.kind ?? "real_estate_office";
  const venueHint = VENUE_HINT[kind];
  const copy = CIVIC_COPY[kind] ?? CIVIC_COPY.real_estate_office!;
  const show = () =>
    setUi(
      kind === "home_builders"
        ? { homeBuildersOpen: true, myHomeOpen: false }
        : kind === "furnishings"
          ? { furnishingsOpen: true, myHomeOpen: false }
          : { realEstateOpen: true, myHomeOpen: false },
    );
  return (
    <div className="stack" data-testid="civic-details">
      {item.civic?.description ? <p style={{ margin: 0 }}>{item.civic.description}</p> : null}
      {inside && !venueHint ? (
        <button type="button" className="btn btn-primary" onClick={show} data-testid="civic-show">
          {copy.show}
        </button>
      ) : inside ? null : (
        <EnterButton item={item} />
      )}
      <p className="hint" style={{ margin: 0 }}>
        {venueHint ?? copy.hint}
      </p>
      <PropertyTasksSection item={item} />
    </div>
  );
}

/** The owner's shortcuts on their own home: Customize (My home) and Upgrade at Home Builders. */
function OwnerHomeActions({ item }: { item: PropertyPublic }) {
  const session = useTownSession();
  const pending = useTownStore((s) => s.ui.transitionPending);
  const driving = useTownStore((s) => s.ui.drivingVehicleId !== null);
  const myHomeId = useMyHomeId();
  if (!session || myHomeId !== item.id) return null;
  return (
    <div className="section" data-testid="owner-home-actions">
      <div className="section-title">Your home</div>
      <div className="row">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => session.openMyHome()} data-testid="customize-home">
          Customize
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={pending || driving || !session.homeBuildersId}
          title={driving ? OUT_OF_CAR_FIRST : undefined}
          onClick={() => session.openShop("home_builders")}
          data-testid="upgrade-at-home-builders"
        >
          Upgrade at Home Builders
        </button>
      </div>
      <span className="hint">Customize places your yard items and furniture; upgrades are sold at Townsy Home Builders, 2 Plaza Way.</span>
    </div>
  );
}

/** The signed-in resident's home id (null for guests / no home); re-renders when it loads. */
function useMyHomeId(): string | null {
  const signedIn = useTownStore((s) => Boolean(s.bootstrap?.session.userId));
  return useMyHomeQuery(signedIn).data?.home?.propertyId ?? null;
}

function ResidentialDetails({ item }: { item: PropertyPublic }) {
  const myHomeId = useMyHomeId();
  const testPrices = useTestPrices();
  const viewer = useViewerActorId();
  const setUi = useTownStore((s) => s.setUi);
  const session = useTownSession();
  const { pending, inOffice, driving } = useTownStore(
    useShallow((s) => ({
      pending: s.ui.transitionPending,
      driving: s.ui.drivingVehicleId !== null,
      inOffice: Boolean(s.room.kind === "interior" && s.room.propertyId && session?.isRealEstateOffice(s.room.propertyId)),
    })),
  );
  const stage = item.home ? (HOME_STAGE_LABELS[item.home.stage] ?? item.home.stage) : null;
  const forSale = isPlotForSale(item);
  const dollar = useDollarGate();

  if (forSale) {
    const officeId = session?.realEstateOfficeId ?? null;
    return (
      <div className="stack" data-testid="lot-for-sale">
        <p style={{ margin: 0 }}>
          <strong>For sale</strong> — buy it at Townsy Real Estate, 1 Plaza Way.
        </p>
        <dl className="kv">
          <dt>Address</dt>
          <dd>{item.address}</dd>
          <dt>District</dt>
          <dd>{districtName(item.districtId) || "—"}</dd>
          <dt>Price in coins</dt>
          <dd className="price">{item.forSale?.coins !== null && item.forSale?.coins !== undefined ? formatCoins(item.forSale.coins) : "—"}</dd>
          <dt>Price in dollars</dt>
          <dd className="price">
            {formatCents(item.forSale?.usdCents ?? null)}
            {item.forSale?.usdCents !== null && item.forSale?.usdCents !== undefined ? (
              <span className="hint" data-testid="lot-usd-hint">
                {" "}
                · {dollar.enabled ? `Townsy Credit first, then ${cardPhrase(dollar.config?.mode)}` : dollar.reason}
              </span>
            ) : null}
          </dd>
        </dl>
        <p className="hint" style={{ margin: 0 }}>
          Two separate offers, not an exchange rate. {testPrices ? "Development test prices. " : null}Your home starts as a tent and can never be taken over by
          other players.
        </p>
        {inOffice ? (
          <button type="button" className="btn btn-primary" onClick={() => setUi({ realEstateOpen: true })}>
            Show plot listings
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!session || !officeId || pending || driving}
            title={driving ? OUT_OF_CAR_FIRST : undefined}
            onClick={() => officeId && void session?.fastTravel(officeId)}
            data-testid="go-to-real-estate"
          >
            {pending ? "Travelling…" : "Go to the Real Estate office"}
          </button>
        )}
        <div className="section">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setUi({ reportOpen: true })}>
            Report this plot
          </button>
        </div>
      </div>
    );
  }

  const closed = item.access === "closed";
  return (
    <div className="stack" data-testid="home-details">
      <p style={{ margin: 0 }}>{item.home ? `A ${stage?.toLowerCase()} on this plot.` : "A residential plot."}</p>
      {closed && myHomeId !== item.id && !isPropertyOwner(item, viewer) ? <ClosedNotice /> : null}
      {/* ADR-0019: a closed home shows its Enter disabled with the reason (the owner's is enabled). */}
      {item.enterable || closed || myHomeId === item.id ? <EnterButton item={item} /> : null}
      <OwnerHomeActions item={item} />
      <div className="section">
        <div className="section-title">Home</div>
        <dl className="kv">
          <dt>Address</dt>
          <dd>{item.address}</dd>
          <dt>District</dt>
          <dd>{districtName(item.districtId) || "—"}</dd>
          <dt>Owner</dt>
          <dd>{item.ownerLabel ?? "Private owner"}</dd>
          <dt>Home level</dt>
          <dd data-testid="home-panel-stage">{stage ?? "—"}</dd>
          {item.home ? (
            <>
              <dt>Look</dt>
              <dd className="small" data-testid="home-panel-look">
                {exteriorLookSummary(item.home.look)}
                <div className="muted">Inside: {interiorLookSummary(item.home.look)}</div>
              </dd>
              <dt>Yard and inside</dt>
              <dd>
                {item.home.placedItems.filter((p) => p.scope !== "interior").length} in the yard ·{" "}
                {item.home.placedItems.filter((p) => p.scope === "interior").length} inside
              </dd>
            </>
          ) : null}
          <dt>Visitors</dt>
          <dd data-testid="home-panel-access">{item.home ? (closed ? "Closed" : "Open") : "—"}</dd>
        </dl>
        <p className="hint" style={{ marginTop: 6 }}>
          Homes can never be taken over by other players. Owners upgrade them one level at a time at Townsy Home Builders, from tent to
          castle.
        </p>
      </div>
      <div className="section">
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setUi({ reportOpen: true })}>
          Report this home
        </button>
      </div>
      <p className="small" style={{ margin: 0 }}>
        <Link to="/directory">All homes in the directory</Link>
      </p>
    </div>
  );
}
